import { getAuthToken } from 'utils/auth.utils';
import {
  REACT_SCRIPT_URL, REACT_DOM_SCRIPT_URL, EMBEDDED_EXPLORER_SCRIPT_URL, EMBEDDED_EXPLORER_DEFAULTS,
} from './EmbeddedExplorer.constants';

/**
 * Utility functions for the Embedded Explorer component
 */

// #genai Preserve the original DOM load event as the cause while rejecting with an Error.
const createScriptLoadError = (message, cause) => {
  const error = new Error(message);
  error.cause = cause;
  return error;
};

/**
 * Loads the React production scripts required for Embedded Explorer
 * @returns {Promise} Promise that resolves when React scripts are loaded
 */
function loadReactScripts() {
  return new Promise((resolve, reject) => {
    // Check if React is already loaded
    if (window.React && window.ReactDOM) {
      resolve();
      return;
    }

    const reactScriptId = 'react-script';
    const reactDomScriptId = 'react-dom-script';

    // Remove existing scripts if any
    const existingReactScript = document.getElementById(reactScriptId);
    const existingReactDomScript = document.getElementById(reactDomScriptId);

    // #genai - Element.remove() detaches the same nodes as head.removeChild()
    if (existingReactScript) {
      existingReactScript.remove();
    }
    if (existingReactDomScript) {
      existingReactDomScript.remove();
    }

    let scriptsLoaded = 0;
    const totalScripts = 2;

    const checkAllLoaded = () => {
      scriptsLoaded += 1;
      if (scriptsLoaded === totalScripts) {
        resolve();
      }
    };

    // Load React
    const reactScript = document.createElement('script');
    reactScript.type = 'text/javascript';
    reactScript.async = true;
    reactScript.defer = true;
    reactScript.id = reactScriptId;
    reactScript.src = REACT_SCRIPT_URL;
    reactScript.crossOrigin = 'anonymous';
    reactScript.onload = checkAllLoaded;
    reactScript.onerror = (error) => reject(createScriptLoadError('Failed to load React script', error));
    document.head.appendChild(reactScript);

    // Load ReactDOM
    const reactDomScript = document.createElement('script');
    reactDomScript.type = 'text/javascript';
    reactDomScript.async = true;
    reactDomScript.defer = true;
    reactDomScript.id = reactDomScriptId;
    reactDomScript.src = REACT_DOM_SCRIPT_URL;
    reactDomScript.crossOrigin = 'anonymous';
    reactDomScript.onload = checkAllLoaded;
    reactDomScript.onerror = (error) => reject(createScriptLoadError('Failed to load ReactDOM script', error));
    document.head.appendChild(reactDomScript);
  });
}

/**
 * Loads the Embedded Explorer widget script
 * @returns {Promise} Promise that resolves when the script is loaded
 */
function loadEmbeddedExplorerScript() {
  const scriptId = 'embedded-explorer-script';
  const existingScript = document.getElementById(scriptId);

  // #genai - Element.remove() detaches the same node as head.removeChild()
  if (existingScript) {
    existingScript.remove();
  }

  return new Promise((resolve, reject) => {
    // Check if the widget is already loaded
    // #genai - optional chaining, same truthiness guard as before
    if (window.ev?.EmbeddedExplorer) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = scriptId;
    script.src = EMBEDDED_EXPLORER_SCRIPT_URL;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = (error) => reject(createScriptLoadError('Failed to load Embedded Explorer script', error));
    document.head.appendChild(script);
  });
}

/**
 * Loads all required scripts for Embedded Explorer
 * @returns {Promise} Promise that resolves when all scripts are loaded
 */
export function loadEmbeddedExplorerApi() {
  return loadReactScripts()
    .then(() => loadEmbeddedExplorerScript())
    .catch((error) => {
      // #genai Keep logging the original DOM event while propagating an Error to satisfy S6671.
      // eslint-disable-next-line no-console
      console.error('Failed to load Embedded Explorer scripts:', error.cause ?? error);
      throw error;
    });
}

/**
 * Creates and initializes an Embedded Explorer map instance
 * @param {string} containerId - The ID of the container element
 * @param {Object} options - Configuration options for the map
 * @param {string} options.apiKey - API key for authentication
 * @param {string} options.authToken - Auth token for authentication (alternative to apiKey)
 * @returns {Object} The map instance
 */
export function createEmbeddedExplorerMap(containerId, options = {}) {
  // #genai - optional chaining, same truthiness guard as before
  if (!window.ev?.EmbeddedExplorer) {
    throw new Error('Embedded Explorer API not loaded. Call loadEmbeddedExplorerApi() first.');
  }

  const mapOptions = {
    ...options,
    ...EMBEDDED_EXPLORER_DEFAULTS,
  };

  const apiKey = process.env.REACT_APP_EMBEDDED_EXPLORER_API_KEY;

  if (apiKey) {
    mapOptions.apiKey = apiKey;
  } else {
    mapOptions.authToken = getAuthToken();
  }

  try {
    const map = new window.ev.EmbeddedExplorer();
    return map.mount(containerId, mapOptions);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to create Embedded Explorer map:', error);
    throw error;
  }
}

export const createEmbeddedExplorerLocationMarker = ({ longitude, latitude }, dragEnabled) => (
  {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
    properties: {
      eagleview: {
        subType: 'Marker',
        svg: `<svg width="48" height="48" viewBox="0 0 36 36" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                  <g fill="blue">
                    <!-- Pin body -->
                    <path d="M12 2c-3.866 0-7 3.134-7 7 0 5.25 7 12 7 12s7-6.75 7-12c0-3.866-3.134-7-7-7z"/>
                    <!-- Inner circle “hole” -->
                    <circle cx="12" cy="9" r="2.5" fill="white"/>
                  </g>
                </svg>`,
        style: {
          size: 48,
        },
        dragEnabled,
      },
    },
  }
);

export const removeEmbeddedExplorerLocationMarkers = (map) => map?.removeFeatures?.({ geoJson: (feature) => feature.geometry.type === 'Point' });
